import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@src/reducers/store";
import { useDashboardActions } from "@src/components/pages/dashboards-page/hooks/use-dashboard-actions";
import {
  updateDashboardPresentation,
} from "@src/reducers/dashboards-slice";
import {
  DashboardColors,
  DashboardPresentation,
} from "@src/types/dashboards";

// 400ms debounce window for picker drags. Same cadence the tile-grid
// uses for its positioning flush (see dashboard-tile-grid.tsx), so the
// picker feels of-a-piece with the rest of the editor.
const DEBOUNCE_MS = 400;

interface UseDashboardColorsEditorArgs {
  // Drives the debounce-flush: when the dashboardId changes (user
  // selects a different dashboard from the left panel), any in-flight
  // palette edit is force-flushed so the previous dashboard's intent
  // doesn't get silently dropped on the floor.
  dashboardId: string;
  // BE-truth palette read out of Redux. Used for the live-preview
  // snapshot (captured at the first drag tick), the reset revert
  // target when there's no pending edit, and the cleanup-flush revert
  // target when there's no pending edit.
  currentColors: DashboardColors | null;
}

interface UseDashboardColorsEditorReturn {
  // Streams drag ticks from the picker. Each call: dispatch optimistic
  // (re-tints canvas immediately), arm 400ms debounced PATCH. Caller
  // passes the FULL palette (already spread with currentColors).
  applyDraft: (nextColors: DashboardColors) => void;
  // Cancel any debounced mid-edit draft, dispatch optimistic
  // `{ colors: null }`, immediately PATCH the same. PATCH can fail —
  // the action hook's revert + snackbar path handles it uniformly
  // with the `applyDraft` flow.
  reset: () => void;
}

// Debounce + flush + live preview + revert machinery for the
// per-dashboard palette editor. Three responsibilities:
//
//   1. Live preview during drag — each picker tick dispatches the
//      new palette to Redux synchronously, so the canvas re-tints
//      with no perceptible lag.
//
//   2. Coalesce rapid drag events into a single PATCH 400ms after the
//      user stops dragging. The action hook from Phase 2 (race-safe
//      sequence counter + revert + snackbar) owns the network leg.
//
//   3. Force-flush on dashboardId change / unmount so a mid-edit color
//      choice lands on BE before the editor unmounts or shifts focus.
//
// Snapshot strategy: the revert target lives in a `snapshotRef`, not
// in any closure. Captured at the FIRST drag tick of a sequence
// (when `pendingColorsRef.current === null`) and held until the
// debounced PATCH fires. That way the revert target stays the
// pre-edit BE truth even if `currentColors` (Redux) reflects an
// intermediate optimistic dispatch.
//
// Why a hook and not a Redux listenerMiddleware: the 400ms debounce
// and the timer cleanup are inherently hook-shaped (refs + effects).
// Middleware would be a heavier abstraction in exchange for one
// mediocre fit for the listenAction pattern.
export const useDashboardColorsEditor = ({
  dashboardId,
  currentColors,
}: UseDashboardColorsEditorArgs): UseDashboardColorsEditorReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const { updateDashboardColors } = useDashboardActions();
  const debounceTimerRef = useRef<number | null>(null);
  const pendingColorsRef = useRef<DashboardColors | null>(null);
  // Snapshot of BE-truth at the moment the user started dragging.
  // Held across ticks so the PATCH's revert target stays the pre-edit
  // BE truth (not the intermediate optimistic value of `currentColors`).
  const snapshotRef = useRef<DashboardPresentation | null>(null);

  // Mirror the parent-supplied currentColors into a ref so
  // `captureSnapshot` has stable identity across renders. Otherwise
  // every optimistic dispatch would rebuild `captureSnapshot`, which
  // in turn would trigger the cleanup effect on every drag tick and
  // defeat the 400ms debounce.
  const currentColorsRef = useRef<DashboardColors | null>(currentColors);
  useEffect(() => {
    currentColorsRef.current = currentColors;
  }, [currentColors]);

  // Single PATCH path. `nextColors === null` is the reset shape
  // (`{ colors: null }` on the wire); otherwise the full palette.
  // Snapshot comes from the caller (ref) — closure-freshness of
  // `currentColors` is irrelevant here, sidestepping the home-truth
  // staleness problem flagged by Phase 5 advisor review.
  const firePatch = useCallback(
    async (
      nextColors: DashboardColors | null,
      prevPresentation: DashboardPresentation | null,
    ) => {
      await updateDashboardColors(
        dashboardId,
        nextColors === null ? { colors: null } : { colors: nextColors },
        prevPresentation,
      );
    },
    [dashboardId, updateDashboardColors],
  );

  // Capture the current value of `currentColors` as a presentation-
  // shaped snapshot for PATCH revert-on-failure. Ref-backed: identity
  // is stable across renders, so this callback stays out of the
  // cleanup effect's dep chain.
  const captureSnapshot = useCallback(
    (): DashboardPresentation | null =>
      currentColorsRef.current
        ? { colors: currentColorsRef.current }
        : null,
    [],
  );

  // Schedule a debounced PATCH. Caller passes the FULL palette
  // (already spread — see dashboard-theme-editor.tsx); we stash,
  // re-tint via optimistic dispatch, and arm a fresh timer.
  const applyDraft = useCallback(
    (nextColors: DashboardColors) => {
      // Live preview: synchronously dispatch the new palette so the
      // canvas re-tints with no perceptible lag, even mid-drag (the
      // debounced PATCH won't fire for another 400ms).
      dispatch(
        updateDashboardPresentation({
          dashboardId,
          presentation: { colors: nextColors },
        }),
      );

      // On the FIRST tick of a sequence (no pending edit held), pin
      // the revert target to current BE-truth. Subsequent ticks within
      // the same drag keep this snapshot — they MUST NOT overwrite it
      // with the in-progress optimistic value.
      if (pendingColorsRef.current === null) {
        snapshotRef.current = captureSnapshot();
      }

      pendingColorsRef.current = nextColors;
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        const pending = pendingColorsRef.current;
        const snapshot = snapshotRef.current;
        debounceTimerRef.current = null;
        pendingColorsRef.current = null;
        snapshotRef.current = null;
        if (pending !== null) {
          void firePatch(pending, snapshot);
        }
      }, DEBOUNCE_MS);
    },
    [dispatch, dashboardId, captureSnapshot, firePatch],
  );

  // Reset = cancel any debounced draft (mid-edit discard) + optimistic
  // dispatch `{ colors: null }` + immediate PATCH. Snapshot for revert
  // comes from either:
  //   - the captured `snapshotRef` if a drag was in flight (preserve
  //     pre-drag BE truth), or
  //   - current closure of `currentColors` if no drag was held (live
  //     BE-truth is the right revert target).
  const reset = useCallback(() => {
    const snapshot =
      pendingColorsRef.current !== null
        ? snapshotRef.current
        : captureSnapshot();

    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingColorsRef.current = null;
    snapshotRef.current = null;

    // Instant "no-theme" feel — same optimistic-dispatch path as
    // applyDraft, but with `colors: null` on the wire.
    dispatch(
      updateDashboardPresentation({
        dashboardId,
        presentation: { colors: null },
      }),
    );

    void firePatch(null, snapshot);
  }, [
    dispatch,
    dashboardId,
    captureSnapshot,
    firePatch,
  ]);

  // Flush on dashboardId change or unmount. The hook lives in
  // DashboardsPage, so unmount happens only on full-page navigation.
  // The 400ms debounce fires the live-edit PATCH within the user's
  // typical interaction window — this cleanup is a belt-and-braces
  // guard for the dashboard-switch case (and any future route change).
  //
  // Strict deps: `[dashboardId]` only. `captureSnapshot` and
  // `firePatch` are stable thanks to the ref-mirror pattern above
  // (no currentColors in their dep arrays anymore), so this effect
  // does NOT re-run on every drag tick — the 400ms debounce holds.
  useEffect(() => {
    return () => {
      const pending = pendingColorsRef.current;
      const snapshot =
        pending !== null ? snapshotRef.current : captureSnapshot();
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingColorsRef.current = null;
      snapshotRef.current = null;
      if (pending !== null) {
        void firePatch(pending, snapshot);
      }
    };
  }, [dashboardId]); // eslint-disable-line react-hooks/exhaustive-deps
  // captureSnapshot is `useCallback([])` (ref-backed); firePatch
  // changes only on dashboardId update or `updateDashboardColors`
  // identity change (the latter is `useCallback([dispatch])`, stable
  // across renders). So the effect is dashboardId-only in practice.

  return { applyDraft, reset };
};
